import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { routes, useOptionalUser } from '@frontend/utils';

import { AppBottomNavbar } from './app-bottom-navbar.component';
import { isNavbarVisible } from './is-navbar-visible';

interface BottomNavbarContextValue {
  isVisible: boolean;
  setExtraContent: (content: ReactNode) => void;
  clearExtraContent: () => void;
}

const BottomNavbarContext = createContext<BottomNavbarContextValue | null>(
  null,
);

export const useBottomNavbar = (): BottomNavbarContextValue => {
  const context = useContext(BottomNavbarContext);

  if (!context) {
    throw new Error(
      'useBottomNavbar must be used within a BottomNavbarProvider',
    );
  }

  return context;
};

// Returns null when rendered outside of a BottomNavbarProvider (e.g. onboarding,
// the version-update screen) instead of throwing, so consumers like `View` can
// safely treat the navbar as not visible in those contexts.
export const useOptionalBottomNavbar = (): BottomNavbarContextValue | null => {
  return useContext(BottomNavbarContext);
};

export const BottomNavbarProvider = ({ children }: PropsWithChildren) => {
  const [extraContent, setExtraContent] = useState<ReactNode>(null);
  const { user } = useOptionalUser();
  const { pathname } = useLocation();

  const isVisible =
    pathname !== routes.profile.notifications &&
    isNavbarVisible(pathname, !!user);

  const value = useMemo(
    () => ({
      isVisible,
      setExtraContent,
      clearExtraContent: () => setExtraContent(null),
    }),
    [isVisible, setExtraContent],
  );

  return (
    <BottomNavbarContext.Provider value={value}>
      {children}
      {isVisible && <AppBottomNavbar extraContent={extraContent} />}
    </BottomNavbarContext.Provider>
  );
};
